'use client'

import { useEffect, useState } from 'react'
import { useAuthStore } from '@/lib/stores/auth-store'
import { useThemeStore } from '@/lib/stores/theme-store'
import { WhiteLabelAssets, BrandedContent } from '@/components/auth/white-label-assets'
import { cn } from '@/lib/cn'

export function GlobalLoading() {
  const { isLoading: authLoading, isNavigating } = useAuthStore()
  const { currentTheme, isLoading: themeLoading } = useThemeStore()
  const [isVisible, setIsVisible] = useState(true)
  const [shouldRender, setShouldRender] = useState(true)

  // Detectar estados de loading (checkAuth é gerenciado pelo AuthProvider)
  const isLoading = authLoading || themeLoading || isNavigating

  // Controlar visibilidade com animações suaves
  useEffect(() => {
    if (isLoading) {
      // Mostrar loading
      setShouldRender(true)
      // Pequeno delay para garantir que o DOM está pronto
      const showTimer = setTimeout(() => setIsVisible(true), 10)

      // Safety timeout: force hide after 8 seconds if still loading
      const safetyTimer = setTimeout(() => {
        if (isLoading) {
          setIsVisible(false)
          // Force render off after animation
          setTimeout(() => setShouldRender(false), 300)
        }
      }, 8000)

      return () => {
        clearTimeout(showTimer)
        clearTimeout(safetyTimer)
      }
    } else {
      // Esconder loading com fade out
      setIsVisible(false)
      // Remover do DOM após animação terminar
      const hideTimer = setTimeout(() => setShouldRender(false), 300)
      return () => clearTimeout(hideTimer)
    }
  }, [isLoading])

  // Não renderizar se não deve estar visível
  if (!shouldRender) {
    return null
  }

  const brandName = currentTheme?.title || 'Mentorfy'

  return (
    <div
      className={cn(
        "fixed inset-0 z-50 bg-background/75 flex items-center justify-center transition-all duration-300 ease-in-out",
        isVisible
          ? "opacity-100 backdrop-blur-sm"
          : "opacity-0 pointer-events-none"
      )}
      style={{
        backdropFilter: isVisible ? 'blur(4px)' : 'blur(0px)',
      }}
    >
      <div className={cn(
        "text-center space-y-6 transition-all duration-500 ease-out",
        isVisible
          ? "opacity-100 scale-100 translate-y-0"
          : "opacity-0 scale-95 translate-y-4"
      )}>
        {/* Logo com animação */}
        <div className="flex justify-center">
          <div className={cn(
            "transition-all duration-700 ease-out",
            isVisible
              ? "opacity-100 scale-100 rotate-0"
              : "opacity-0 scale-110 rotate-12"
          )}>
            <BrandedContent fallback={null}>
              <WhiteLabelAssets
                type="logo"
                className="h-12 w-auto"
                alt={`${brandName} Logo`}
              />
            </BrandedContent>
          </div>
        </div>

        {/* Brand info com slide up */}
        <div className={cn(
          "space-y-2 text-center max-w-sm mx-auto transition-all duration-600 ease-out",
          isVisible
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-8"
        )}>
          <p className="text-sm text-muted-foreground">
            {isNavigating
              ? 'Redirecionando você para a plataforma...'
              : ' '
            }
          </p>
        </div>

        {/* Loading dots com animação staggered */}
        <div className={cn(
          "flex justify-center space-x-1 transition-all duration-500 ease-out",
          isVisible
            ? "opacity-100 scale-100"
            : "opacity-0 scale-75"
        )}>
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className={cn(
                "h-2 w-2 bg-primary rounded-full transition-all duration-1000",
                isVisible ? "animate-pulse" : ""
              )}
              style={{
                animationDelay: `${i * 0.15}s`,
                animationDuration: '1.5s',
                transform: isVisible ? 'scale(1)' : 'scale(0)',
                transitionDelay: `${i * 50}ms`
              }}
            />
          ))}
        </div>

        {/* Pulse effect background */}
        <div className={cn(
          "absolute inset-0 -z-10 transition-all duration-1000",
          isVisible
            ? "scale-150 opacity-100"
            : "scale-100 opacity-0"
        )} />
      </div>
    </div>
  )
}